import React, { useState } from 'react';
import { Calculator, Copy, Trash2, TrendingDown } from 'lucide-react';

const InflationCalculatorTool = () => {
  const [initialAmount, setInitialAmount] = useState('');
  const [inflationRate, setInflationRate] = useState('');
  const [years, setYears] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const calculate = () => {
    const amount = parseFloat(initialAmount);
    const rate = parseFloat(inflationRate);
    const t = parseFloat(years);

    if (isNaN(amount) || isNaN(rate) || isNaN(t) || amount <= 0 || t <= 0) {
      setResult(null);
      return;
    }

    // Purchasing power after inflation
    // Adjusted Value = Amount / (1 + rate)^years
    const adjustedValue = amount / Math.pow(1 + rate / 100, t);
    const purchasingPowerLost = amount - adjustedValue;
    const percentLost = (purchasingPowerLost / amount) * 100;

    // What things will cost in the future
    // Future Cost = Amount * (1 + rate)^years
    const futureCost = amount * Math.pow(1 + rate / 100, t);
    const costIncrease = futureCost - amount;
    const percentIncrease = (costIncrease / amount) * 100;

    setResult({
      amount,
      rate,
      years: t,
      adjustedValue,
      purchasingPowerLost,
      percentLost,
      futureCost,
      costIncrease,
      percentIncrease
    });
  };

  const copyResult = (value) => {
    navigator.clipboard.writeText(value.toFixed(2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const clearAll = () => {
    setInitialAmount('');
    setInflationRate('');
    setYears('');
    setResult(null);
  };

  const formatCurrency = (value) => {
    return '$' + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <div className="space-y-6">
      {/* Input Section */}
      <div className="p-4 bg-gray-800 rounded-lg space-y-4">
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Initial Amount ($)
            </label>
            <input
              type="number"
              value={initialAmount}
              onChange={(e) => setInitialAmount(e.target.value)}
              placeholder="e.g., 1000"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="initial-amount-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Inflation Rate (% per year)
            </label>
            <input
              type="number"
              step="0.1"
              value={inflationRate}
              onChange={(e) => setInflationRate(e.target.value)}
              placeholder="e.g., 3"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="inflation-rate-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Years
            </label>
            <input
              type="number"
              value={years}
              onChange={(e) => setYears(e.target.value)}
              placeholder="e.g., 10"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="years-input"
            />
          </div>
        </div>
      </div>

      {/* Calculate Button */}
      <button
        onClick={calculate}
        className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
        data-testid="calculate-inflation-btn"
      >
        <Calculator size={18} />
        Calculate Inflation Impact
      </button>

      {/* Results Section */}
      {result && (
        <div className="space-y-4" data-testid="inflation-results">
          {/* Purchasing Power Section */}
          <div className="p-4 bg-gray-800 rounded-lg">
            <div className="flex items-center gap-2 mb-4">
              <TrendingDown size={18} className="text-red-400" />
              <h3 className="text-sm font-medium text-gray-300">Purchasing Power Impact</h3>
            </div>
            
            <div className="p-4 bg-gray-900 rounded-lg mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">Adjusted Value (Today's Dollars)</span>
                <button
                  onClick={() => copyResult(result.adjustedValue)}
                  className={`p-1.5 rounded transition-colors ${
                    copied ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                  data-testid="copy-adjusted-value"
                >
                  <Copy size={14} />
                </button>
              </div>
              <div className="text-2xl font-bold text-red-400" data-testid="adjusted-value-result">
                {formatCurrency(result.adjustedValue)}
              </div>
              <div className="text-sm text-gray-500 mt-1">
                -{result.percentLost.toFixed(1)}% purchasing power
              </div>
            </div>

            <div className="p-4 bg-red-900/20 border border-red-800/50 rounded-lg">
              <p className="text-sm text-gray-300">
                <span className="text-white font-medium">{formatCurrency(result.amount)}</span> today will only buy what{' '}
                <span className="text-red-400 font-medium">{formatCurrency(result.adjustedValue)}</span> buys now in{' '}
                <span className="text-white font-medium">{result.years} years</span>.
              </p>
              <p className="text-xs text-gray-500 mt-2">
                You lose {formatCurrency(result.purchasingPowerLost)} in purchasing power.
              </p>
            </div>
          </div>

          {/* Future Cost Section */}
          <div className="p-4 bg-gray-800 rounded-lg">
            <h3 className="text-sm font-medium text-gray-300 mb-4">Future Cost Projection</h3>
            
            <div className="p-4 bg-gray-900 rounded-lg mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">Future Cost</span>
                <button
                  onClick={() => copyResult(result.futureCost)}
                  className={`p-1.5 rounded transition-colors ${
                    copied ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                  data-testid="copy-future-cost"
                >
                  <Copy size={14} />
                </button>
              </div>
              <div className="text-2xl font-bold text-yellow-400" data-testid="future-cost-result">
                {formatCurrency(result.futureCost)}
              </div>
              <div className="text-sm text-gray-500 mt-1">
                +{result.percentIncrease.toFixed(1)}% increase
              </div>
            </div>

            <div className="p-4 bg-yellow-900/20 border border-yellow-800/50 rounded-lg">
              <p className="text-sm text-gray-300">
                Something that costs <span className="text-white font-medium">{formatCurrency(result.amount)}</span> today will cost{' '}
                <span className="text-yellow-400 font-medium">{formatCurrency(result.futureCost)}</span> in{' '}
                <span className="text-white font-medium">{result.years} years</span>.
              </p>
              <p className="text-xs text-gray-500 mt-2">
                An increase of {formatCurrency(result.costIncrease)}.
              </p>
            </div>
          </div>

          {/* Summary */}
          <div className="p-4 bg-gray-800 rounded-lg">
            <h4 className="text-sm font-medium text-gray-300 mb-3">Calculation Summary</h4>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Initial Amount</span>
                <span className="text-white font-medium">{formatCurrency(result.amount)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Inflation Rate</span>
                <span className="text-white font-medium">{result.rate}% per year</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Time Period</span>
                <span className="text-white font-medium">{result.years} years</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Explanation Section */}
      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-2">What is Purchasing Power?</h3>
        <p className="text-sm text-gray-400">
          Purchasing power is the value of money in terms of what it can buy. As prices rise due to inflation, 
          each dollar buys less than before. This means your savings lose real value over time unless they 
          grow faster than inflation.
        </p>
      </div>

      {/* Info Section */}
      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-3">Historical Inflation Rates</h3>
        <div className="grid md:grid-cols-3 gap-4 text-sm text-gray-400">
          <div>
            <div className="text-gray-500 mb-1">US Average (Long-term)</div>
            <div>~3% per year</div>
          </div>
          <div>
            <div className="text-gray-500 mb-1">Recent Years (2022-2024)</div>
            <div>3% - 8% per year</div>
          </div>
          <div>
            <div className="text-gray-500 mb-1">Fed Target Rate</div>
            <div>2% per year</div>
          </div>
        </div>
      </div>

      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-2">Beating Inflation</h3>
        <div className="text-sm text-gray-400 space-y-1">
          <div>• Invest in assets that historically outpace inflation (stocks, real estate)</div>
          <div>• Consider I-Bonds or TIPS for inflation-protected savings</div>
          <div>• Keep only emergency funds in low-yield savings accounts</div>
        </div>
      </div>

      {/* Clear Button */}
      <button
        onClick={clearAll}
        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
        data-testid="clear-inflation-btn"
      >
        <Trash2 size={16} />
        Clear All
      </button>
    </div>
  );
};

export default InflationCalculatorTool;
